import {css, useTheme} from '@mui/material';
import {
  DlMuiTypography,
  GetStylesFunction,
  DlMuiRadioGroup,
  utils,
  DlMuiSuccessCheck,
  enumContainer,
  ConfigObjectType,
  useApiMutation,
  CHANGED_BY_OTHER_USER_STATUS,
  useChangedByOtherDialog,
} from '@wallaby-ui/core';
import {parseISO} from 'date-fns';
import * as React from 'react';
import {useState, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {useStore} from 'react-redux';
import {useParams} from 'react-router-dom';
import {useFormatters} from 'src/js/common';
import {MerieSchema} from 'src/js/common/types';

import {EditableField} from './EditableField';
import {EditableFieldWrapper} from './EditableFieldWrapper';
import {EditIconWrapper} from './EditIconWrapper';
import {usePermissions} from '../../permissions/usePermissions';

interface OwnProps {
  patientCardEtag: string | undefined;
  data: MerieSchema.components['schemas']['PatientCardDto'];
  reloadPatientCard: () => void;
  config: ConfigObjectType;
}

type Props = OwnProps;

export const EvaluationStateEdit: React.FC<Props> = (props) => {
  const {patientCardEtag, data, reloadPatientCard, config, ...restProps} = props;
  const intl = useIntl();
  const theme = useTheme();
  const styles = getStyles({theme});
  const params = useParams();
  const state = useStore()?.getState();
  const enumMap = enumContainer.getEnumMap(state);
  const {t, tEnum} = useFormatters();

  const [showEditButtonCheck, setShowEditButtonCheck] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showSuccessWindow, setShowSuccessWindow] = useState<boolean>(false);
  const [timestamp, setTimestamp] = useState<string | undefined>(data.evaluationStateModifiedOn ?? undefined);
  const [checkRadioValue, setCheckRadioValue] = useState<string>(data.evaluationState);

  const handleOnMouseEnterCheck = () => {
    setShowEditButtonCheck(true);
  };
  const handleOnMouseLeaveCheck = () => {
    setShowEditButtonCheck(false);
  };

  const handleOnClickCheck = () => {
    setIsEditing(true);
  };

  const handleDiscardCheck = () => {
    setIsEditing(false);
    setCheckRadioValue(data.evaluationState);
  };

  const handleCloseSuccessWindow = () => {
    setShowSuccessWindow(false);
  };

  const formattedCheckTime = timestamp && utils.DateUtils.getFormattedDate(parseISO(timestamp), intl, false, true);

  const evaluationStateApiUrl = `${config.service.url.hospitalizationDetail}/${params.hospitalizationId}/patient-card/evaluation-state`;

  const translatedLabelReturnToHospitalizations = intl && utils.TranslateUtils.translate('patient.card.returnToHospitalizations', intl);

  const checkRadioGroupEnum = enumMap.EvaluationStateEnum.filter((enumItem) => enumItem.name !== 'UNCHECKED');

  const checkRadioGroupOptions = useMemo(
    () =>
      checkRadioGroupEnum.map((enumItem) => {
        const translatedRiskFactorTypeEnumValue = intl && utils.TranslateUtils.translate(enumItem.messageKey, intl);

        return {
          translatedLabel: translatedRiskFactorTypeEnumValue,
          value: enumItem.name,
        };
      }),
    [checkRadioGroupEnum, intl]
  );

  const onSuccess = async () => {
    reloadPatientCard();
  };
  const {changedByOtherDialog, showChangedByOtherDialog} = useChangedByOtherDialog({
    handleConfirm: () => {
      reloadPatientCard();
    },
  });
  const onError = async (error) => {
    if (error?.response?.status === CHANGED_BY_OTHER_USER_STATUS)
      showChangedByOtherDialog({
        [t('patient.card.check')]: tEnum('EvaluationStateEnum', checkRadioValue),
      });
    else reloadPatientCard();
  };

  const {mutate: evaluationStateMutate} = useApiMutation(
    [evaluationStateApiUrl, patientCardEtag],
    {evaluationState: checkRadioValue},
    {
      reactMutationOptions: {
        onSuccess,
        onError,
      },
      axiosConfig: {
        data: {evaluationState: checkRadioValue},
        method: 'PATCH',
        url: evaluationStateApiUrl,
        headers: {
          'If-Match': patientCardEtag,
        },
      },
    },
    true
  );

  const handleOnSubmitCheck = () => {
    if (checkRadioValue === 'CHECKED') {
      setShowSuccessWindow(true);
    }
    setTimestamp(new Date().toISOString());
    evaluationStateMutate();
    setIsEditing(false);
  };

  const handleOnEvaluationStateChange = (_event, value) => {
    setCheckRadioValue(value);
  };

  const {userRoles} = usePermissions(config);
  return (
    <EditableFieldWrapper {...restProps}>
      {showSuccessWindow ? (
        <DlMuiSuccessCheck
          showSuccessWindow={showSuccessWindow}
          handleCloseSuccessWindow={handleCloseSuccessWindow}
          modifiedTime={formattedCheckTime ?? undefined}
          buttonTitle={translatedLabelReturnToHospitalizations}
          displayValue={'patient.card.check.success'}
        />
      ) : (
        <EditableField
          handleOnMouseEnter={handleOnMouseEnterCheck}
          handleOnMouseLeave={handleOnMouseLeaveCheck}
          label={'patient.card.check'}
        >
          {isEditing ? (
            <DlMuiRadioGroup
              options={checkRadioGroupOptions}
              selectedValue={checkRadioValue}
              handleSave={handleOnSubmitCheck}
              handleDiscard={handleDiscardCheck}
              handleOnChange={handleOnEvaluationStateChange}
            />
          ) : (
            <EditIconWrapper
              showEditButton={showEditButtonCheck && userRoles.ENCOUNTER_DETAIL_EDIT_ROLE}
              handleOnClick={handleOnClickCheck}
              showAlertIcon={data.evaluationState === 'UNCHECKED'}
              alertIconTitle={'patient.card.chip.evaluationAlert'}
            >
              <div css={styles.checkedMessageWrapper}>
                <DlMuiTypography variant={'value'} enumName={'EvaluationStateEnum'} enumValue={data.evaluationState} />
                <DlMuiTypography variant={'value'} value={formattedCheckTime ?? undefined} />
              </div>
            </EditIconWrapper>
          )}
        </EditableField>
      )}
      {changedByOtherDialog}
    </EditableFieldWrapper>
  );
};

EvaluationStateEdit.displayName = 'EvaluationStateEdit';

const getStyles: GetStylesFunction = (_options) => ({
  checkedMessageWrapper: css`
    display: flex;
    flex-direction: column;
    align-items: center;
  `,
});
